import { createRouter, ErrorComponentProps } from '@tanstack/react-router';
import { routeTree } from './routeTree.gen';
import Error500 from './components/Errors/Error500';

/**
 * Custom Error Component for TanStack Router
 * Shows a user-friendly error page with the ability to reset
 */
function RouterErrorComponent({ error, reset }: ErrorComponentProps) {
  // Log error for debugging
  console.error('Router error:', error);

  // If it's a route-level error, show Error500 with more details
  return (
    <div className="w-error w-error--500 d-flex align-items-center flex-column justify-content-center">
      <Error500 />
      {/* Optional: Show error details in development */}
      {process.env.NODE_ENV === 'development' && (
        <div className="mt-4 p-4 bg-light border rounded" style={{ maxWidth: '600px' }}>
          <h5 className="text-danger">Error Details (Development Only):</h5>
          <pre className="text-sm">{error.message}</pre>
          {reset && (
            <button className="e-button e-button--primary mt-2" onClick={reset}>
              Try Again
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Router factory for TanStack Start. Start calls it once in the browser and once per
 * request on the server, so it must return a new router every time. The `Register`
 * declaration is generated into routeTree.gen.ts.
 */
export function getRouter() {
  return createRouter({
    routeTree,
    defaultPreload: 'intent',

    // Show a minimal loading state instead of flashing 404 during route transitions
    defaultPendingComponent: () => (
      <div className="w-page">
        <div className="w-page-content d-flex align-items-center justify-content-center">
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      </div>
    ),

    // Default error component for route-level errors
    defaultErrorComponent: RouterErrorComponent,

    // Small delay to prevent flashing on fast navigations
    defaultPendingMs: 100,
    defaultPendingMinMs: 500,

    // 404 handling is now managed by the catch-all route: client/routes/$splat.tsx
  });
}
